import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  UnauthorizedError,
  conversationsApi,
  type ConversationDTO,
  type ConversationMessageDTO,
  type UUID,
} from '@/shared/api'
import { useAuth } from '@/features/auth'
import { useScope } from '@/features/scope'
import type { RagScope } from '@/features/scope'
import { useReferenceSelection } from '@/features/references'

/**
 * Sentinel meaning "no conversation selected — render the prompt-first home
 * screen". Kept as a string to preserve the existing AppShell branching
 * without an extra type union.
 */
export const DRAFT_CONVERSATION_ID = 'draft'

export type ActiveConversationId = UUID | typeof DRAFT_CONVERSATION_ID

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error'

interface ConversationsState {
  list: ConversationDTO[]
  listStatus: LoadStatus
  listError: string | null

  activeConversationId: ActiveConversationId
  messagesByConversation: Record<UUID, ConversationMessageDTO[]>
  messagesStatusByConversation: Record<UUID, LoadStatus>
  messagesErrorByConversation: Record<UUID, string | null>

  /** Assistant message currently selected for the sources panel. */
  selectedAssistantMessageId: UUID | null

  sending: boolean
  sendError: string | null
}

interface ConversationsContextValue extends ConversationsState {
  refreshList: () => Promise<void>
  selectConversation: (id: UUID) => void
  startNewConsultation: () => void
  sendMessage: (content: string) => Promise<void>
  deleteConversation: (id: UUID) => Promise<void>
  renameConversation: (id: UUID, title: string) => Promise<void>
  selectAssistantMessage: (id: UUID | null) => void
}

const ConversationsContext = createContext<ConversationsContextValue | null>(null)

const INITIAL_STATE: ConversationsState = {
  list: [],
  listStatus: 'idle',
  listError: null,
  activeConversationId: DRAFT_CONVERSATION_ID,
  messagesByConversation: {},
  messagesStatusByConversation: {},
  messagesErrorByConversation: {},
  selectedAssistantMessageId: null,
  sending: false,
  sendError: null,
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

function sortConversations(items: ConversationDTO[]): ConversationDTO[] {
  return [...items].sort((a, b) => {
    const pinDiff = (b.pinned_at ? 1 : 0) - (a.pinned_at ? 1 : 0)
    if (pinDiff !== 0) return pinDiff
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })
}

interface ProviderProps {
  children: ReactNode
}

/**
 * Build the legacy ``scope_*`` payload from a single ``RagScope`` instance.
 * Kept around so single-document selections continue to surface entity/
 * group/document_id fields on the wire — that keeps the backend's
 * single-doc path (and any client that still reads
 * ``conversation.scope_*``) backward compatible with the multi-doc rollout
 * in Step 8.3. Multi-doc selections deliberately skip these fields: N case
 * documents can cross entity/group tuples, and intersecting them would
 * silently drop chunks.
 */
function singleScopePayload(scope: RagScope | null): {
  scope_entity_type?: string | null
  scope_entity_id?: string | null
  scope_group_type?: string | null
  scope_group_id?: string | null
  scope_document_id?: string | null
} {
  if (!scope) return {}
  return {
    scope_entity_type: scope.entity_type ?? undefined,
    scope_entity_id: scope.entity_id ?? undefined,
    scope_group_type: scope.group_type ?? undefined,
    scope_group_id: scope.group_id ?? undefined,
    scope_document_id: scope.document_id ?? undefined,
  }
}

export function ConversationsProvider({ children }: ProviderProps) {
  const { organization, status: authStatus, logout } = useAuth()
  const { selected: activeScope, selectedCaseDocuments } = useScope()
  const { selectedIds: selectedReferenceIds } = useReferenceSelection()
  const [state, setState] = useState<ConversationsState>(INITIAL_STATE)
  const listRequestedRef = useRef(false)

  const handleAuthFailure = useCallback(async () => {
    setState(INITIAL_STATE)
    listRequestedRef.current = false
    await logout()
  }, [logout])

  const refreshList = useCallback(async () => {
    setState((prev) => ({ ...prev, listStatus: 'loading', listError: null }))
    try {
      const response = await conversationsApi.list()
      setState((prev) => ({
        ...prev,
        // ``?? []`` guards the state updater (run during render) against a
        // malformed envelope — see the same note in ScopeProvider.reload.
        list: sortConversations(response.items ?? []),
        listStatus: 'ready',
        listError: null,
      }))
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await handleAuthFailure()
        return
      }
      setState((prev) => ({
        ...prev,
        listStatus: 'error',
        listError: errorMessage(err, 'Falha ao carregar conversas.'),
      }))
    }
  }, [handleAuthFailure])

  // Boot conversations list once authenticated.
  useEffect(() => {
    if (authStatus !== 'authenticated') {
      listRequestedRef.current = false
      return
    }
    if (listRequestedRef.current) return
    listRequestedRef.current = true
    void refreshList()
  }, [authStatus, refreshList])

  const loadMessages = useCallback(
    async (conversationId: UUID) => {
      setState((prev) => ({
        ...prev,
        messagesStatusByConversation: {
          ...prev.messagesStatusByConversation,
          [conversationId]: 'loading',
        },
        messagesErrorByConversation: {
          ...prev.messagesErrorByConversation,
          [conversationId]: null,
        },
      }))
      try {
        const response = await conversationsApi.getMessages(conversationId)
        setState((prev) => {
          // ``?? []`` guards the state updater (run during render) against a
          // malformed envelope — see the note in ScopeProvider.reload.
          const items = response.items ?? []
          const firstAssistant = items.find((m) => m.role === 'assistant')
          const lastAssistant = [...items]
            .reverse()
            .find((m) => m.role === 'assistant')
          const preferred = lastAssistant ?? firstAssistant ?? null
          return {
            ...prev,
            messagesByConversation: {
              ...prev.messagesByConversation,
              [conversationId]: items,
            },
            messagesStatusByConversation: {
              ...prev.messagesStatusByConversation,
              [conversationId]: 'ready',
            },
            messagesErrorByConversation: {
              ...prev.messagesErrorByConversation,
              [conversationId]: null,
            },
            selectedAssistantMessageId:
              prev.activeConversationId === conversationId
                ? preferred?.id ?? null
                : prev.selectedAssistantMessageId,
          }
        })
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await handleAuthFailure()
          return
        }
        setState((prev) => ({
          ...prev,
          messagesStatusByConversation: {
            ...prev.messagesStatusByConversation,
            [conversationId]: 'error',
          },
          messagesErrorByConversation: {
            ...prev.messagesErrorByConversation,
            [conversationId]: errorMessage(err, 'Falha ao carregar mensagens.'),
          },
        }))
      }
    },
    [handleAuthFailure],
  )

  const selectConversation = useCallback(
    (id: UUID) => {
      setState((prev) => ({
        ...prev,
        activeConversationId: id,
        selectedAssistantMessageId: null,
        sendError: null,
      }))
      const existing = state.messagesByConversation[id]
      const status = state.messagesStatusByConversation[id]
      if (!existing && status !== 'loading') {
        void loadMessages(id)
      } else if (existing) {
        const lastAssistant = [...existing].reverse().find((m) => m.role === 'assistant')
        setState((prev) => ({
          ...prev,
          selectedAssistantMessageId: lastAssistant?.id ?? null,
        }))
      }
    },
    [loadMessages, state.messagesByConversation, state.messagesStatusByConversation],
  )

  const startNewConsultation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeConversationId: DRAFT_CONVERSATION_ID,
      selectedAssistantMessageId: null,
      sendError: null,
    }))
  }, [])

  const sendMessage = useCallback(
    async (rawContent: string) => {
      const content = rawContent.trim()
      if (!content) return
      if (!organization) {
        setState((prev) => ({
          ...prev,
          sendError: 'Sessão sem organização ativa.',
        }))
        return
      }
      setState((prev) => ({ ...prev, sending: true, sendError: null }))
      try {
        // Step 8.3 — pin the multi-doc case selection AT SEND TIME. The
        // user may keep toggling rows in the Scope Selector after pressing
        // Enter; the in-flight message must still carry the snapshot they
        // meant to ask with. Empty list ⇒ omit ``case_document_ids`` and
        // fall back to the legacy single-scope path (``scope_*``).
        const caseDocumentIdsForRequest =
          selectedCaseDocuments.length > 0
            ? selectedCaseDocuments.map((d) => d.document_id)
            : undefined
        // Single-doc selection still emits the legacy scope_* fields so
        // the conversation row / message envelope keep a meaningful
        // entity/group breadcrumb. Multi-doc selections cross-cut entity/
        // group tuples, so we skip scope_* entirely and rely purely on
        // case_document_ids for retrieval (see backend pipeline 8.3).
        const scopeForRequest =
          selectedCaseDocuments.length <= 1
            ? singleScopePayload(activeScope)
            : {}
        // Step 8.1 — pin the reference selection AT SEND TIME. If the user
        // toggles references after pressing Enter, the in-flight message must
        // still carry the snapshot they meant to ask with. Empty list ⇒ omit
        // from payload (preserves legacy single-pass behavior server-side).
        const referenceIdsForRequest =
          selectedReferenceIds.length > 0
            ? [...selectedReferenceIds]
            : undefined
        let conversationId: UUID
        if (state.activeConversationId === DRAFT_CONVERSATION_ID) {
          const created = await conversationsApi.create({
            tenant_id: organization.tenant_id,
            ...scopeForRequest,
          })
          conversationId = created.id
          setState((prev) => ({
            ...prev,
            list: sortConversations([created, ...prev.list]),
            activeConversationId: created.id,
            messagesByConversation: {
              ...prev.messagesByConversation,
              [created.id]: [],
            },
            messagesStatusByConversation: {
              ...prev.messagesStatusByConversation,
              [created.id]: 'ready',
            },
          }))
        } else {
          conversationId = state.activeConversationId
        }

        const response = await conversationsApi.postMessage(conversationId, {
          content,
          ...scopeForRequest,
          ...(referenceIdsForRequest
            ? { reference_document_ids: referenceIdsForRequest }
            : {}),
          ...(caseDocumentIdsForRequest
            ? { case_document_ids: caseDocumentIdsForRequest }
            : {}),
        })

        setState((prev) => {
          const prior = prev.messagesByConversation[conversationId] ?? []
          // Step 13.7 — anexa o flag de baixa confiança e os warnings (que
          // vivem no envelope, não na mensagem) à mensagem do assistant para
          // o balão exibir o selo + aviso. Não persistido: ao recarregar o
          // histórico esses campos voltam undefined (aceitável — o selo é
          // mais relevante logo após perguntar).
          const assistantWithMeta: ConversationMessageDTO = {
            ...response.assistant_message,
            low_confidence: response.low_confidence,
            warnings: response.warnings,
          }
          const next = [...prior, response.user_message, assistantWithMeta]
          // Bump conversation's position in the list (most recent first).
          const updatedList = prev.list.map((c) =>
            c.id === conversationId
              ? { ...c, updated_at: response.assistant_message.created_at }
              : c,
          )
          return {
            ...prev,
            sending: false,
            sendError: null,
            messagesByConversation: {
              ...prev.messagesByConversation,
              [conversationId]: next,
            },
            messagesStatusByConversation: {
              ...prev.messagesStatusByConversation,
              [conversationId]: 'ready',
            },
            selectedAssistantMessageId: response.assistant_message.id,
            list: sortConversations(updatedList),
          }
        })

        // Refresh list to capture server-generated title updates.
        void refreshList()
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await handleAuthFailure()
          return
        }
        setState((prev) => ({
          ...prev,
          sending: false,
          sendError: errorMessage(err, 'Falha ao enviar mensagem.'),
        }))
      }
    },
    [
      organization,
      state.activeConversationId,
      refreshList,
      handleAuthFailure,
      activeScope,
      selectedCaseDocuments,
      selectedReferenceIds,
    ],
  )

  const deleteConversation = useCallback(
    async (id: UUID) => {
      try {
        await conversationsApi.remove(id)
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await handleAuthFailure()
          return
        }
        setState((prev) => ({
          ...prev,
          listError: errorMessage(err, 'Falha ao remover conversa.'),
        }))
        return
      }
      setState((prev) => ({
        ...prev,
        list: prev.list.filter((c) => c.id !== id),
        activeConversationId:
          prev.activeConversationId === id
            ? DRAFT_CONVERSATION_ID
            : prev.activeConversationId,
      }))
    },
    [handleAuthFailure],
  )

  const renameConversation = useCallback(
    async (id: UUID, title: string) => {
      const trimmed = title.trim()
      if (!trimmed) return
      // Optimistic — patch the local cache immediately so the rename feels
      // instant. Reverted from `previousTitle` if the request fails.
      let previousTitle: string | undefined
      setState((prev) => ({
        ...prev,
        list: prev.list.map((c) => {
          if (c.id !== id) return c
          previousTitle = c.title
          return { ...c, title: trimmed }
        }),
      }))
      try {
        const updated = await conversationsApi.patch(id, { title: trimmed })
        setState((prev) => ({
          ...prev,
          list: sortConversations(
            prev.list.map((c) => (c.id === id ? { ...c, ...updated } : c)),
          ),
        }))
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await handleAuthFailure()
          return
        }
        setState((prev) => ({
          ...prev,
          list: prev.list.map((c) =>
            c.id === id && previousTitle !== undefined
              ? { ...c, title: previousTitle }
              : c,
          ),
          listError: errorMessage(err, 'Falha ao renomear conversa.'),
        }))
      }
    },
    [handleAuthFailure],
  )

  const selectAssistantMessage = useCallback((id: UUID | null) => {
    setState((prev) => ({ ...prev, selectedAssistantMessageId: id }))
  }, [])

  const value = useMemo<ConversationsContextValue>(
    () => ({
      ...state,
      refreshList,
      selectConversation,
      startNewConsultation,
      sendMessage,
      deleteConversation,
      renameConversation,
      selectAssistantMessage,
    }),
    [
      state,
      refreshList,
      selectConversation,
      startNewConsultation,
      sendMessage,
      deleteConversation,
      renameConversation,
      selectAssistantMessage,
    ],
  )

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  )
}

export function useConversations(): ConversationsContextValue {
  const ctx = useContext(ConversationsContext)
  if (!ctx) {
    throw new Error('useConversations must be used inside <ConversationsProvider>')
  }
  return ctx
}
